/**
 * Comprehensive Error Handling System for Image Upload and Display
 * 
 * This module provides structured error classification, parsing utilities,
 * and user-friendly error messages with actionable suggestions.
 */

// Error type constants
export const ERROR_TYPES = {
  // Network-related errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  CONNECTION_TIMEOUT: 'CONNECTION_TIMEOUT',
  DNS_ERROR: 'DNS_ERROR',
  OFFLINE: 'OFFLINE',
  
  // File validation errors
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  INVALID_FILE_TYPE: 'INVALID_FILE_TYPE',
  INVALID_FILE_EXTENSION: 'INVALID_FILE_EXTENSION',
  CORRUPTED_FILE: 'CORRUPTED_FILE',
  EMPTY_FILE: 'EMPTY_FILE',
  
  // Server errors
  SERVER_ERROR: 'SERVER_ERROR',
  SERVER_UNAVAILABLE: 'SERVER_UNAVAILABLE',
  STORAGE_FULL: 'STORAGE_FULL',
  PROCESSING_ERROR: 'PROCESSING_ERROR',
  
  // Permission errors
  PERMISSION_ERROR: 'PERMISSION_ERROR',
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  AUTHORIZATION_ERROR: 'AUTHORIZATION_ERROR',
  QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
  
  // Client errors
  BROWSER_NOT_SUPPORTED: 'BROWSER_NOT_SUPPORTED',
  MEMORY_ERROR: 'MEMORY_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  
  // Asset-specific errors
  ASSET_NOT_FOUND: 'ASSET_NOT_FOUND',
  IMAGE_NOT_FOUND: 'IMAGE_NOT_FOUND',
  DUPLICATE_IMAGE: 'DUPLICATE_IMAGE',
  
  // Generic fallback
  GENERIC_ERROR: 'GENERIC_ERROR'
};

// Error severity levels
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// Action types for error recovery
export const ERROR_ACTIONS = {
  RETRY: 'retry',
  REFRESH: 'refresh',
  CONTACT_SUPPORT: 'contact_support',
  CHECK_CONNECTION: 'check_connection',
  REDUCE_FILE_SIZE: 'reduce_file_size',
  CHANGE_FILE_FORMAT: 'change_file_format',
  LOGIN_AGAIN: 'login_again',
  WAIT_AND_RETRY: 'wait_and_retry',
  CLEAR_CACHE: 'clear_cache',
  UPDATE_BROWSER: 'update_browser'
};

/**
 * Comprehensive error message mapping with user-friendly messages and actionable suggestions
 */
export const ERROR_MESSAGES = {
  [ERROR_TYPES.NETWORK_ERROR]: {
    title: 'Connection Problem',
    message: 'Unable to connect to the server. Please check your internet connection.',
    suggestion: 'Check your internet connection and try again.',
    action: ERROR_ACTIONS.CHECK_CONNECTION,
    severity: ERROR_SEVERITY.HIGH,
    icon: 'wifi-off',
    retryable: true
  },
  
  [ERROR_TYPES.CONNECTION_TIMEOUT]: {
    title: 'Request Timeout',
    message: 'The request took too long to complete.',
    suggestion: 'Your connection might be slow. Try again or check your network.',
    action: ERROR_ACTIONS.RETRY,
    severity: ERROR_SEVERITY.MEDIUM,
    icon: 'clock',
    retryable: true
  },
  
  [ERROR_TYPES.DNS_ERROR]: {
    title: 'Server Not Found',
    message: 'Cannot reach the server. There might be a DNS issue.',
    suggestion: 'Check your internet connection or try again later.',
    action: ERROR_ACTIONS.CHECK_CONNECTION,
    severity: ERROR_SEVERITY.HIGH,
    icon: 'server-off',
    retryable: true
  },
  
  [ERROR_TYPES.OFFLINE]: {
    title: 'You\'re Offline',
    message: 'No internet connection detected.',
    suggestion: 'Connect to the internet and try again.',
    action: ERROR_ACTIONS.CHECK_CONNECTION,
    severity: ERROR_SEVERITY.HIGH,
    icon: 'wifi-off',
    retryable: false
  },
  
  [ERROR_TYPES.FILE_TOO_LARGE]: {
    title: 'File Too Large',
    message: 'The selected file exceeds the maximum size limit of 5MB.',
    suggestion: 'Compress the image or choose a smaller file.',
    action: ERROR_ACTIONS.REDUCE_FILE_SIZE,
    severity: ERROR_SEVERITY.MEDIUM,
    icon: 'file-x',
    retryable: false
  },
  
  [ERROR_TYPES.INVALID_FILE_TYPE]: {
    title: 'Invalid File Type',
    message: 'Only JPEG, PNG, and WebP images are supported.',
    suggestion: 'Convert your file to a supported format (JPEG, PNG, or WebP).',
    action: ERROR_ACTIONS.CHANGE_FILE_FORMAT,
    severity: ERROR_SEVERITY.MEDIUM,
    icon: 'image-x',
    retryable: false
  },
  
  [ERROR_TYPES.INVALID_FILE_EXTENSION]: {
    title: 'Invalid File Extension',
    message: 'File extension not supported.',
    suggestion: 'Use files with .jpg, .jpeg, .png, or .webp extensions.',
    action: ERROR_ACTIONS.CHANGE_FILE_FORMAT,
    severity: ERROR_SEVERITY.MEDIUM,
    icon: 'file-type',
    retryable: false
  },
  
  [ERROR_TYPES.CORRUPTED_FILE]: {
    title: 'Corrupted File',
    message: 'The file appears to be corrupted or damaged.',
    suggestion: 'Try a different file or re-save the original image.',
    action: ERROR_ACTIONS.CHANGE_FILE_FORMAT,
    severity: ERROR_SEVERITY.MEDIUM,
    icon: 'file-warning',
    retryable: false
  },
  
  [ERROR_TYPES.EMPTY_FILE]: {
    title: 'Empty File',
    message: 'The selected file is empty or has no content.',
    suggestion: 'Choose a valid image file with content.',
    action: ERROR_ACTIONS.CHANGE_FILE_FORMAT,
    severity: ERROR_SEVERITY.MEDIUM,
    icon: 'file-x',
    retryable: false
  },
  
  [ERROR_TYPES.SERVER_ERROR]: {
    title: 'Server Error',
    message: 'The server encountered an error while processing your request.',
    suggestion: 'Try again in a few moments. If the problem persists, contact support.',
    action: ERROR_ACTIONS.WAIT_AND_RETRY,
    severity: ERROR_SEVERITY.HIGH,
    icon: 'server-crash',
    retryable: true
  },
  
  [ERROR_TYPES.SERVER_UNAVAILABLE]: {
    title: 'Service Unavailable',
    message: 'The server is temporarily unavailable.',
    suggestion: 'The service might be under maintenance. Try again later.',
    action: ERROR_ACTIONS.WAIT_AND_RETRY,
    severity: ERROR_SEVERITY.HIGH,
    icon: 'server-off',
    retryable: true
  },
  
  [ERROR_TYPES.STORAGE_FULL]: {
    title: 'Storage Full',
    message: 'Server storage is full and cannot accept new uploads.',
    suggestion: 'Contact your administrator to free up storage space.',
    action: ERROR_ACTIONS.CONTACT_SUPPORT,
    severity: ERROR_SEVERITY.CRITICAL,
    icon: 'hard-drive-x',
    retryable: false
  },
  
  [ERROR_TYPES.PROCESSING_ERROR]: {
    title: 'Processing Failed',
    message: 'Failed to process the image file.',
    suggestion: 'The file might be corrupted. Try a different image.',
    action: ERROR_ACTIONS.CHANGE_FILE_FORMAT,
    severity: ERROR_SEVERITY.MEDIUM,
    icon: 'image-x',
    retryable: true
  },
  
  [ERROR_TYPES.PERMISSION_ERROR]: {
    title: 'Permission Denied',
    message: 'You don\'t have permission to upload images.',
    suggestion: 'Contact your administrator for proper permissions.',
    action: ERROR_ACTIONS.CONTACT_SUPPORT,
    severity: ERROR_SEVERITY.HIGH,
    icon: 'shield-x',
    retryable: false
  },
  
  [ERROR_TYPES.AUTHENTICATION_ERROR]: {
    title: 'Authentication Required',
    message: 'You need to log in to upload images.',
    suggestion: 'Please log in and try again.',
    action: ERROR_ACTIONS.LOGIN_AGAIN,
    severity: ERROR_SEVERITY.HIGH,
    icon: 'user-x',
    retryable: false
  },
  
  [ERROR_TYPES.AUTHORIZATION_ERROR]: {
    title: 'Access Denied',
    message: 'You\'re not authorized to perform this action.',
    suggestion: 'Contact your administrator for access rights.',
    action: ERROR_ACTIONS.CONTACT_SUPPORT,
    severity: ERROR_SEVERITY.HIGH,
    icon: 'lock',
    retryable: false
  },
  
  [ERROR_TYPES.QUOTA_EXCEEDED]: {
    title: 'Upload Limit Reached',
    message: 'You\'ve reached the maximum number of images allowed.',
    suggestion: 'Remove some existing images before uploading new ones.',
    action: ERROR_ACTIONS.CONTACT_SUPPORT,
    severity: ERROR_SEVERITY.MEDIUM,
    icon: 'image-plus',
    retryable: false
  },
  
  [ERROR_TYPES.BROWSER_NOT_SUPPORTED]: {
    title: 'Browser Not Supported',
    message: 'Your browser doesn\'t support this feature.',
    suggestion: 'Update your browser or use a modern browser like Chrome or Firefox.',
    action: ERROR_ACTIONS.UPDATE_BROWSER,
    severity: ERROR_SEVERITY.MEDIUM,
    icon: 'browser',
    retryable: false
  },
  
  [ERROR_TYPES.MEMORY_ERROR]: {
    title: 'Memory Error',
    message: 'Not enough memory to process the image.',
    suggestion: 'Try closing other tabs or use a smaller image file.',
    action: ERROR_ACTIONS.REDUCE_FILE_SIZE,
    severity: ERROR_SEVERITY.MEDIUM,
    icon: 'memory-stick',
    retryable: true
  },
  
  [ERROR_TYPES.VALIDATION_ERROR]: {
    title: 'Validation Error',
    message: 'The request contains invalid data.',
    suggestion: 'Check your input and try again.',
    action: ERROR_ACTIONS.RETRY,
    severity: ERROR_SEVERITY.MEDIUM,
    icon: 'alert-triangle',
    retryable: true
  },
  
  [ERROR_TYPES.ASSET_NOT_FOUND]: {
    title: 'Asset Not Found',
    message: 'The asset you\'re trying to update doesn\'t exist.',
    suggestion: 'Refresh the page and try again.',
    action: ERROR_ACTIONS.REFRESH,
    severity: ERROR_SEVERITY.HIGH,
    icon: 'search-x',
    retryable: true
  },
  
  [ERROR_TYPES.IMAGE_NOT_FOUND]: {
    title: 'Image Not Found',
    message: 'The requested image could not be found.',
    suggestion: 'The image might have been deleted. Refresh the page.',
    action: ERROR_ACTIONS.REFRESH,
    severity: ERROR_SEVERITY.MEDIUM,
    icon: 'image-off',
    retryable: true
  },
  
  [ERROR_TYPES.DUPLICATE_IMAGE]: {
    title: 'Duplicate Image',
    message: 'This image has already been uploaded.',
    suggestion: 'Choose a different image or skip this upload.',
    action: ERROR_ACTIONS.CHANGE_FILE_FORMAT,
    severity: ERROR_SEVERITY.LOW,
    icon: 'copy',
    retryable: false
  },
  
  [ERROR_TYPES.GENERIC_ERROR]: {
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred.',
    suggestion: 'Try again or contact support if the problem persists.',
    action: ERROR_ACTIONS.RETRY,
    severity: ERROR_SEVERITY.MEDIUM,
    icon: 'alert-circle',
    retryable: true
  }
};

/**
 * Parse backend error responses into structured error objects
 * @param {Error|Object} error - The error object from axios or other sources
 * @param {Object} context - Additional context about the operation
 * @returns {Object} Structured error object
 */
export const parseBackendError = (error, context = {}) => {
  // Handle network errors (no response from server)
  if (!error.response) {
    // Check if user is offline first
    if (!navigator.onLine) {
      return createErrorObject(ERROR_TYPES.OFFLINE, 'No internet connection', context);
    }
    
    if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
      return createErrorObject(ERROR_TYPES.NETWORK_ERROR, error.message, context);
    }
    
    if (error.code === 'ENOTFOUND' || error.message?.includes('getaddrinfo ENOTFOUND')) {
      return createErrorObject(ERROR_TYPES.DNS_ERROR, error.message, context);
    }
    
    if (error.code === 'TIMEOUT' || error.message?.includes('timeout')) {
      return createErrorObject(ERROR_TYPES.CONNECTION_TIMEOUT, error.message, context);
    }
    
    return createErrorObject(ERROR_TYPES.NETWORK_ERROR, error.message || 'Network connection failed', context);
  }

  // Handle structured error responses from backend
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    
    // If detail is an object with error_type, use structured error
    if (typeof detail === 'object' && detail.error_type) {
      return createErrorObject(
        detail.error_type,
        detail.message || 'An error occurred',
        { ...context, details: detail.details, retry_after: detail.retry_after }
      );
    }
    
    // If detail is a string, parse it for known error patterns
    if (typeof detail === 'string') {
      return parseErrorString(detail, context);
    }
  }
  
  // Handle HTTP status codes
  return parseHttpStatusError(error.response, context);
};

/**
 * Parse error strings for known patterns
 * @param {string} errorString - The error message string
 * @param {Object} context - Additional context
 * @returns {Object} Structured error object
 */
const parseErrorString = (errorString, context) => {
  const lowerError = errorString.toLowerCase();
  
  // File type errors
  if (lowerError.includes('file type') || lowerError.includes('invalid file type')) {
    return createErrorObject(ERROR_TYPES.INVALID_FILE_TYPE, errorString, context);
  }
  
  // File size errors
  if (lowerError.includes('file size') || lowerError.includes('too large') || lowerError.includes('exceeds')) {
    return createErrorObject(ERROR_TYPES.FILE_TOO_LARGE, errorString, context);
  }
  
  // Permission errors
  if (lowerError.includes('permission') || lowerError.includes('unauthorized') || lowerError.includes('forbidden')) {
    return createErrorObject(ERROR_TYPES.PERMISSION_ERROR, errorString, context);
  }
  
  // Authentication errors
  if (lowerError.includes('authentication') || lowerError.includes('login') || lowerError.includes('token')) {
    return createErrorObject(ERROR_TYPES.AUTHENTICATION_ERROR, errorString, context);
  }
  
  // Not found errors
  if (lowerError.includes('not found') || lowerError.includes('does not exist')) {
    if (lowerError.includes('asset')) {
      return createErrorObject(ERROR_TYPES.ASSET_NOT_FOUND, errorString, context);
    }
    if (lowerError.includes('image')) {
      return createErrorObject(ERROR_TYPES.IMAGE_NOT_FOUND, errorString, context);
    }
    return createErrorObject(ERROR_TYPES.ASSET_NOT_FOUND, errorString, context);
  }
  
  // Storage errors
  if (lowerError.includes('storage') || lowerError.includes('disk full') || lowerError.includes('no space')) {
    return createErrorObject(ERROR_TYPES.STORAGE_FULL, errorString, context);
  }
  
  // Processing errors
  if (lowerError.includes('processing') || lowerError.includes('corrupted') || lowerError.includes('invalid image')) {
    return createErrorObject(ERROR_TYPES.PROCESSING_ERROR, errorString, context);
  }
  
  // Quota errors
  if (lowerError.includes('quota') || lowerError.includes('limit') || lowerError.includes('maximum')) {
    return createErrorObject(ERROR_TYPES.QUOTA_EXCEEDED, errorString, context);
  }
  
  // Duplicate errors
  if (lowerError.includes('duplicate') || lowerError.includes('already exists')) {
    return createErrorObject(ERROR_TYPES.DUPLICATE_IMAGE, errorString, context);
  }
  
  // Default to server error for unrecognized strings
  return createErrorObject(ERROR_TYPES.SERVER_ERROR, errorString, context);
};

/**
 * Parse HTTP status codes into appropriate error types
 * @param {Object} response - The HTTP response object
 * @param {Object} context - Additional context
 * @returns {Object} Structured error object
 */
const parseHttpStatusError = (response, context) => {
  const status = response?.status;
  const statusText = response?.statusText || '';
  
  switch (status) {
    case 400:
      return createErrorObject(ERROR_TYPES.VALIDATION_ERROR, 
        'Invalid request. Please check your input and try again.', context);
    
    case 401:
      return createErrorObject(ERROR_TYPES.AUTHENTICATION_ERROR, 
        'Authentication required. Please log in and try again.', context);
    
    case 403:
      return createErrorObject(ERROR_TYPES.AUTHORIZATION_ERROR, 
        'You don\'t have permission to perform this action.', context);
    
    case 404:
      return createErrorObject(ERROR_TYPES.ASSET_NOT_FOUND, 
        'The requested resource was not found.', context);
    
    case 413:
      return createErrorObject(ERROR_TYPES.FILE_TOO_LARGE, 
        'File size exceeds the maximum limit.', context);
    
    case 415:
      return createErrorObject(ERROR_TYPES.INVALID_FILE_TYPE, 
        'Unsupported file type.', context);
    
    case 429:
      return createErrorObject(ERROR_TYPES.QUOTA_EXCEEDED, 
        'Too many requests. Please wait before trying again.', 
        { ...context, retry_after: response.headers?.['retry-after'] });
    
    case 500:
      return createErrorObject(ERROR_TYPES.SERVER_ERROR, 
        'Internal server error. Please try again later.', context);
    
    case 502:
    case 503:
      return createErrorObject(ERROR_TYPES.SERVER_UNAVAILABLE, 
        'Service temporarily unavailable. Please try again later.', context);
    
    case 507:
      return createErrorObject(ERROR_TYPES.STORAGE_FULL, 
        'Server storage is full.', context);
    
    default:
      return createErrorObject(ERROR_TYPES.GENERIC_ERROR, 
        `HTTP ${status}: ${statusText}`, context);
  }
};

/**
 * Create a structured error object
 * @param {string} type - Error type from ERROR_TYPES
 * @param {string} message - Error message
 * @param {Object} context - Additional context and metadata
 * @returns {Object} Structured error object
 */
const createErrorObject = (type, message, context = {}) => {
  const errorConfig = ERROR_MESSAGES[type] || ERROR_MESSAGES[ERROR_TYPES.GENERIC_ERROR];
  
  return {
    type,
    message: message || errorConfig.message,
    title: errorConfig.title,
    suggestion: errorConfig.suggestion,
    action: errorConfig.action,
    severity: errorConfig.severity,
    icon: errorConfig.icon,
    retryable: errorConfig.retryable,
    timestamp: new Date().toISOString(),
    context: {
      operation: context.operation || 'unknown',
      filename: context.filename,
      fileSize: context.fileSize,
      assetId: context.assetId,
      retryCount: context.retryCount || 0,
      details: context.details,
      retry_after: context.retry_after
    }
  };
};

/**
 * Get user-friendly error message with actionable suggestions
 * @param {string} errorType - Error type from ERROR_TYPES
 * @param {Object} context - Additional context for dynamic messages
 * @returns {Object} Error message object with title, message, and suggestion
 */
export const getErrorMessage = (errorType, context = {}) => {
  const errorConfig = ERROR_MESSAGES[errorType] || ERROR_MESSAGES[ERROR_TYPES.GENERIC_ERROR];
  const actualType = ERROR_MESSAGES[errorType] ? errorType : ERROR_TYPES.GENERIC_ERROR;
  
  // Create dynamic messages based on context
  let message = errorConfig.message;
  let suggestion = errorConfig.suggestion;
  
  // Add file-specific information if available
  if (context.filename) {
    message = `${message} (File: ${context.filename})`;
  }
  
  if (context.fileSize && actualType === ERROR_TYPES.FILE_TOO_LARGE) {
    const sizeMB = (context.fileSize / (1024 * 1024)).toFixed(2);
    message = `File "${context.filename}" is ${sizeMB}MB, which exceeds the 5MB limit.`;
    suggestion = `Compress the image to under 5MB or choose a smaller file.`;
  }
  
  if (context.retryCount > 0) {
    suggestion = `${suggestion} (Attempt ${context.retryCount + 1})`;
  }
  
  return {
    ...errorConfig,
    type: actualType,
    message,
    suggestion,
    context
  };
};

/**
 * Validate file before upload and return any validation errors
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @returns {Array} Array of error objects
 */
export const validateFile = (file, options = {}) => {
  const errors = [];
  const {
    maxSize = 64 * 1024 * 1024, // 64MB default
    allowedTypes = ['image/jpeg', 'image/png', 'image/webp'],
    allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp']
  } = options;
  
  // Check if file exists
  if (!file) {
    errors.push(createErrorObject(ERROR_TYPES.EMPTY_FILE, 'No file selected', { operation: 'validation' }));
    return errors;
  }
  
  // Check file size
  if (file.size === 0) {
    errors.push(createErrorObject(ERROR_TYPES.EMPTY_FILE, 'File is empty', { 
      operation: 'validation', 
      filename: file.name 
    }));
  } else if (file.size > maxSize) {
    errors.push(createErrorObject(ERROR_TYPES.FILE_TOO_LARGE, 'File exceeds size limit', { 
      operation: 'validation', 
      filename: file.name, 
      fileSize: file.size 
    }));
  }
  
  // Check file type
  if (!allowedTypes.includes(file.type)) {
    errors.push(createErrorObject(ERROR_TYPES.INVALID_FILE_TYPE, 'Invalid file type', { 
      operation: 'validation', 
      filename: file.name,
      fileType: file.type,
      details: file.type
    }));
  }
  
  // Check file extension
  const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  if (!allowedExtensions.includes(extension)) {
    errors.push(createErrorObject(ERROR_TYPES.INVALID_FILE_EXTENSION, 'Invalid file extension', { 
      operation: 'validation', 
      filename: file.name,
      extension,
      details: extension
    }));
  }
  
  return errors;
};

/**
 * Check if an error is retryable based on its type and context
 * @param {Object} error - Structured error object
 * @returns {boolean} Whether the error is retryable
 */
export const isRetryableError = (error) => {
  if (!error || typeof error !== 'object') return false;
  
  // Check the retryable flag from error configuration
  const errorConfig = ERROR_MESSAGES[error.type];
  if (errorConfig && errorConfig.retryable === false) {
    return false;
  }
  
  // Check retry count
  const retryCount = error.context?.retryCount || 0;
  if (retryCount >= 3) {
    return false;
  }
  
  // Network and server errors are generally retryable
  const retryableTypes = [
    ERROR_TYPES.NETWORK_ERROR,
    ERROR_TYPES.CONNECTION_TIMEOUT,
    ERROR_TYPES.SERVER_ERROR,
    ERROR_TYPES.SERVER_UNAVAILABLE,
    ERROR_TYPES.PROCESSING_ERROR,
    ERROR_TYPES.GENERIC_ERROR
  ];
  
  return retryableTypes.includes(error.type);
};

/**
 * Get suggested actions for error recovery
 * @param {Object} error - Structured error object
 * @returns {Array} Array of suggested actions
 */
export const getErrorActions = (error) => {
  if (!error || !error.action) return [];
  
  const actions = {
    [ERROR_ACTIONS.RETRY]: {
      label: 'Try Again',
      type: 'primary',
      handler: 'retry'
    },
    [ERROR_ACTIONS.REFRESH]: {
      label: 'Refresh Page',
      type: 'secondary',
      handler: 'refresh'
    },
    [ERROR_ACTIONS.CHECK_CONNECTION]: {
      label: 'Check Connection',
      type: 'secondary',
      handler: 'checkConnection'
    },
    [ERROR_ACTIONS.REDUCE_FILE_SIZE]: {
      label: 'Compress Image',
      type: 'secondary',
      handler: 'compressImage'
    },
    [ERROR_ACTIONS.CHANGE_FILE_FORMAT]: {
      label: 'Choose Different File',
      type: 'secondary',
      handler: 'changeFile'
    },
    [ERROR_ACTIONS.LOGIN_AGAIN]: {
      label: 'Log In Again',
      type: 'primary',
      handler: 'login'
    },
    [ERROR_ACTIONS.CONTACT_SUPPORT]: {
      label: 'Contact Support',
      type: 'secondary',
      handler: 'contactSupport'
    },
    [ERROR_ACTIONS.WAIT_AND_RETRY]: {
      label: 'Wait and Retry',
      type: 'primary',
      handler: 'waitAndRetry'
    },
    [ERROR_ACTIONS.CLEAR_CACHE]: {
      label: 'Clear Cache',
      type: 'secondary',
      handler: 'clearCache'
    },
    [ERROR_ACTIONS.UPDATE_BROWSER]: {
      label: 'Update Browser',
      type: 'secondary',
      handler: 'updateBrowser'
    }
  };
  
  const primaryAction = actions[error.action];
  const result = primaryAction ? [primaryAction] : [];
  
  // Add common secondary actions based on error type
  if (error.retryable && error.action !== ERROR_ACTIONS.RETRY) {
    result.push(actions[ERROR_ACTIONS.RETRY]);
  }
  
  if (error.severity === ERROR_SEVERITY.HIGH || error.severity === ERROR_SEVERITY.CRITICAL) {
    result.push(actions[ERROR_ACTIONS.CONTACT_SUPPORT]);
  }
  
  return result;
};